import axios from "axios";
import {
  IndianRupee,
  User,
  Calendar,
  Clock,
  Briefcase,
  Mail,
  Phone,
  Globe,
  Building2,
  MapPin,
  RefreshCcw,
  ArrowLeft,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  formatDate,
  getFormattedBookingDateTime,
} from "../../../utils/helpers";
import StatusBadge from "../../common/StatusBadge";
import Breadcrumb from "../BreadCrumb";

const WorkingHoursSkeleton = () => {
  return (
    <div className="space-y-6 animate-pulse">
      {/* Header Skeleton */}
      <div className="flex items-center justify-between">
        <div className="h-9 w-24 bg-gray-200 rounded-lg" />
        <div className="h-5 w-40 bg-gray-200 rounded" />
        <div className="h-9 w-24 bg-gray-200 rounded-lg" />
      </div>

      {/* Calendar Skeleton */}
      <div className="bg-white rounded-lg border p-4">
        {/* Week headers */}
        <div className="grid grid-cols-7 gap-2 mb-3">
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className="h-4 bg-gray-200 rounded" />
          ))}
        </div>

        {/* Days */}
        <div className="grid grid-cols-7 gap-2">
          {Array.from({ length: 35 }).map((_, i) => (
            <div key={i} className="aspect-square bg-gray-100 rounded-lg" />
          ))}
        </div>
      </div>

      {/* Monthly Summary Skeleton */}
      <div className="bg-gray-50 p-6 rounded-lg border">
        <div className="h-5 w-40 bg-gray-200 rounded mb-4" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-20 bg-gray-200 rounded-lg" />
          ))}
        </div>
      </div>
    </div>
  );
};

const StaffDetails = () => {
  const { staffId } = useParams();
  const navigate = useNavigate();
  const [staff, setStaff] = useState(null);
  const [activeTab, setActiveTab] = useState("working hours");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [workingHours, setWorkingHours] = useState([]);
  const [hoursLoading, setHoursLoading] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [staffLoading, setStaffLoading] = useState(true);

  const BOOKINGS_PER_PAGE = 5;

  const [bookingPage, setBookingPage] = useState(1);
  const totalBookingPages = Math.ceil(bookings.length / BOOKINGS_PER_PAGE);

  const paginatedBookings = bookings.slice(
    (bookingPage - 1) * BOOKINGS_PER_PAGE,
    bookingPage * BOOKINGS_PER_PAGE
  );

  useEffect(() => {
    setBookingPage(1);
  }, [activeTab, staffId]);



  // Fetch staff details
  useEffect(() => {
    const fetchStaffDetails = async () => {
      setStaffLoading(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/admins/user-by-id/${staffId}`
        );
        setStaff(res.data.data);
      } catch (err) {
        console.error("Failed to fetch staff details", err);
      } finally {
        setStaffLoading(false);
      }
    };

    if (staffId) {
      fetchStaffDetails();
    }
  }, [staffId]);

  // Fetch bookings
  useEffect(() => {
    if (!staffId) return;

    const fetchBookings = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/service-bookings/get-all/by-staff-id/${staffId}`
        );
        setBookings(res?.data?.data || []);
      } catch (err) {
        console.error("Failed to fetch staff bookings", err);
        setBookings([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [staffId]);

  // Fetch working hours
  useEffect(() => {
    if (!staffId || activeTab !== "working hours") return;

    const fetchWorkingHours = async () => {
      setHoursLoading(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/staff-assignment/working-hours/${staffId}`
        );
        setWorkingHours(res.data.data || []);
      } catch (err) {
        console.error("Failed to fetch working hours", err);
        setWorkingHours([]);
      } finally {
        setHoursLoading(false);
      }
    };

    fetchWorkingHours();
  }, [staffId, activeTab]);

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const getWorkingHoursForDate = (dateStr) => {
    return workingHours.find((wh) => {
      const whDate = new Date(wh.date).toDateString();
      const checkDate = new Date(dateStr).toDateString();
      return whDate === checkDate;
    });
  };

  const getStatusForDay = (hours, minutes) => {
    const totalMinutes = (hours || 0) * 60 + (minutes || 0);

    if (totalMinutes === 0) {
      return {
        type: "absent",
        label: "Absent",
        color: "bg-red-100 border-red-300 text-red-700",
      };
    } else if (totalMinutes < 60) {
      // Less than 1 hour
      return {
        type: "partial",
        label: "Partial",
        color: "bg-yellow-100 border-yellow-300 text-yellow-700",
      };
    } else if (totalMinutes >= 120) {
      // 2 hours or more
      return {
        type: "full",
        label: "Full Day",
        color: "bg-green-100 border-green-300 text-green-700",
      };
    } else {
      return {
        type: "present",
        label: "Present",
        color: "bg-blue-100 border-blue-300 text-blue-700",
      };
    }
  };

  const renderCalendar = () => {
    const { daysInMonth, startingDayOfWeek, year, month } =
      getDaysInMonth(currentMonth);
    const days = [];
    const today = new Date();

    // Empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="aspect-square" />);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = new Date(year, month, day).toISOString();
      const workData = getWorkingHoursForDate(dateStr);
      const isToday =
        day === today.getDate() &&
        month === today.getMonth() &&
        year === today.getFullYear();
      const isFutureDate = new Date(year, month, day) > today;

      let status = null;
      if (workData && !isFutureDate) {
        status = getStatusForDay(workData.hours, workData.minutes);
      }

      days.push(
        <div
          key={day}
          className={`aspect-square border rounded-lg p-1 sm:p-2 flex flex-col items-center justify-center relative transition-all hover:shadow-md ${isToday ? "ring-2 ring-primaryColor bg-brandAccentColor/20" : "bg-white"
            } ${isFutureDate ? "opacity-40" : ""}`}
        >
          <span
            className={`text-xs sm:text-sm font-medium mb-1 ${isToday ? "text-primaryColor" : "text-gray-700"
              }`}
          >
            {day}
          </span>

          {!isFutureDate && (
            <>
              {workData ? (
                <div className="w-full flex flex-col items-center gap-0.5 sm:gap-1">
                  <div
                    className={`w-full px-1 py-0.5 rounded text-[9px] sm:text-[10px] font-semibold text-center border ${status.color}`}
                  >
                    {status.label}
                  </div>
                  <span className="text-[9px] sm:text-xs font-semibold text-gray-600">
                    {workData.hours}h {workData.minutes}m
                  </span>
                </div>
              ) : (
                <div className="w-full px-1 py-0.5 rounded text-[9px] sm:text-[10px] font-medium text-center border bg-gray-100 border-gray-300 text-gray-500">
                  No Data
                </div>
              )}
            </>
          )}

          {isToday && (
            <div className="absolute top-0.5 right-0.5 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-primaryColor rounded-full" />
          )}
        </div>
      );
    }

    return days;
  };

  const changeMonth = (direction) => {
    setCurrentMonth((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  if (staffLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading staff details...</p>
        </div>
      </div>
    );
  }

  if (!staff) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600">Staff not found</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb */}
      <div className="px-4 py-3 bg-white border-b">
        <Breadcrumb title1="Staff Management" title2={staff.name} />
      </div>

      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <ArrowLeft size={20} />
            </button>

            <div className="flex items-center gap-4">
              {staff.profilePicture ? (
                <img
                  src={staff.profilePicture}
                  alt={staff.name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-gray-200 shadow-sm"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primaryColor via-brandAccentColor to-primaryColor text-white flex items-center justify-center text-2xl font-bold">
                  {staff.name?.charAt(0)?.toUpperCase()}
                </div>
              )}

              <div>
                <h1 className="text-xl font-bold text-gray-900">{staff.name}</h1>
                <p className="text-sm text-gray-600">
                  {staff.designation || "Staff Member"}
                </p>
                {staff.assignedBranch?.branchName && (
                  <p className="text-xs text-gray-500 mt-1">
                    Branch: {staff.assignedBranch.branchName}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex overflow-x-auto">
            {[
              ["working hours", "Working Hours"],
              ["bookings", "Recent Bookings"],
              ["details", "Details"],
            ].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`px-6 py-4 border-b-2 transition whitespace-nowrap text-sm font-medium ${activeTab === key
                  ? "border-primaryBtnColor text-primaryBtnColor"
                  : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {activeTab === "working hours" && (
          <>
            {hoursLoading ? (
              <WorkingHoursSkeleton />
            ) : (
              <div className="space-y-6">
                {/* Calendar Header */}
                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={() => changeMonth(-1)}
                    className="px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition text-sm font-medium shadow-sm"
                  >
                    Previous
                  </button>
                  <h3 className="text-lg font-semibold text-gray-800">
                    {monthNames[currentMonth.getMonth()]}{" "}
                    {currentMonth.getFullYear()}
                  </h3>
                  <button
                    onClick={() => changeMonth(1)}
                    className="px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition text-sm font-medium shadow-sm"
                  >
                    Next
                  </button>
                </div>

                {/* Calendar */}
                <div className="bg-white rounded-lg border shadow-sm p-4">
                  {/* Day headers */}
                  <div className="grid grid-cols-7 gap-2 mb-3">
                    {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                      (day) => (
                        <div
                          key={day}
                          className="text-center text-xs font-semibold text-gray-600 py-2"
                        >
                          {day}
                        </div>
                      )
                    )}
                  </div>

                  {/* Calendar grid */}
                  <div className="grid grid-cols-7 gap-2">{renderCalendar()}</div>
                </div>

                {/* Monthly Summary */}
                {workingHours.length > 0 && (
                  <div className="mt-6 bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200 shadow-sm">
                    <h4 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <Calendar size={18} />
                      Monthly Summary
                    </h4>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className="bg-white p-4 rounded-lg border shadow-sm">
                        <div className="text-xs text-gray-600 mb-1">
                          Total Days
                        </div>
                        <div className="text-2xl font-bold text-gray-800">
                          {
                            workingHours.filter((wh) => {
                              const whDate = new Date(wh.date);
                              return (
                                whDate.getMonth() === currentMonth.getMonth() &&
                                whDate.getFullYear() ===
                                currentMonth.getFullYear()
                              );
                            }).length
                          }
                        </div>
                      </div>
                      <div className="bg-white p-4 rounded-lg border shadow-sm">
                        <div className="text-xs text-green-600 mb-1">
                          Full Days
                        </div>
                        <div className="text-2xl font-bold text-green-600">
                          {
                            workingHours.filter((wh) => {
                              const whDate = new Date(wh.date);
                              const sameMonth =
                                whDate.getMonth() === currentMonth.getMonth() &&
                                whDate.getFullYear() ===
                                currentMonth.getFullYear();
                              return (
                                sameMonth &&
                                wh.hours * 60 + wh.minutes >= 120
                              );
                            }).length
                          }
                        </div>
                      </div>
                      <div className="bg-white p-4 rounded-lg border shadow-sm">
                        <div className="text-xs text-yellow-600 mb-1">
                          Partial Days
                        </div>
                        <div className="text-2xl font-bold text-yellow-600">
                          {
                            workingHours.filter((wh) => {
                              const whDate = new Date(wh.date);
                              const sameMonth =
                                whDate.getMonth() === currentMonth.getMonth() &&
                                whDate.getFullYear() ===
                                currentMonth.getFullYear();
                              const totalMinutes = wh.hours * 60 + wh.minutes;
                              return (
                                sameMonth && totalMinutes > 0 && totalMinutes < 60
                              );
                            }).length
                          }
                        </div>
                      </div>
                      <div className="bg-white p-4 rounded-lg border shadow-sm">
                        <div className="text-xs text-red-600 mb-1">
                          Absent Days
                        </div>
                        <div className="text-2xl font-bold text-red-600">
                          {
                            workingHours.filter((wh) => {
                              const whDate = new Date(wh.date);
                              const sameMonth =
                                whDate.getMonth() === currentMonth.getMonth() &&
                                whDate.getFullYear() ===
                                currentMonth.getFullYear();
                              return (
                                sameMonth && wh.hours === 0 && wh.minutes === 0
                              );
                            }).length
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {activeTab === "details" && (
          <div className="max-w-4xl mx-auto space-y-8">
            {/* Personal Information */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2 border-b pb-2">
                <User size={18} />
                Personal Information
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InfoCard label="Full Name" value={staff.name} icon={User} />
                <InfoCard
                  label="Designation"
                  value={staff.designation}
                  icon={Briefcase}
                />
                <InfoCard
                  label="Email Address"
                  value={staff.email}
                  icon={Mail}
                />
                <InfoCard
                  label="Phone Number"
                  value={staff.phoneNumber}
                  icon={Phone}
                />
                <InfoCard
                  label="Gender"
                  value={staff.gender || "---"}
                  icon={User}
                  capitalize
                />
                <InfoCard
                  label="Date of Birth"
                  value={
                    staff.dateOfBirth ? formatDate(staff.dateOfBirth) : "---"
                  }
                  icon={Calendar}
                />
                <InfoCard
                  label="Nationality"
                  value={staff.nationality || "---"}
                  icon={Globe}
                />
                <InfoCard
                  label="Branch"
                  value={staff.assignedBranch?.branchName || "---"}
                  icon={Building2}
                />
              </div>
            </div>

            {/* Address */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2 border-b pb-2">
                <MapPin size={18} />
                Address Information
              </h3>

              <div className="bg-gray-50 p-4 rounded-xl border flex items-start gap-3">
                <MapPin className="text-primaryBtnColor mt-1" size={18} />
                <div>
                  <div className="text-xs text-gray-500 font-medium mb-1">
                    Full Address
                  </div>
                  <div className="text-sm text-gray-900">
                    {staff.address || "No address provided"}
                  </div>
                </div>
              </div>
            </div>

            {/* Statistics */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2 border-b pb-2">
                <IndianRupee size={18} />
                Statistics & Timeline
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <StatCard
                  label="Total Bookings"
                  value={bookings.length}
                  icon={Briefcase}
                  color="bg-blue-500"
                />
                <StatCard
                  label="Joined On"
                  value={staff.createdAt ? formatDate(staff.createdAt) : "---"}
                  icon={Calendar}
                  color="bg-green-500"
                />
                <StatCard
                  label="Last Updated"
                  value={staff.updatedAt ? formatDate(staff.updatedAt) : "---"}
                  icon={RefreshCcw}
                  color="bg-purple-500"
                />
              </div>
            </div>
          </div>
        )}

        {/* Bookings */}
        {activeTab === "bookings" && (
          <>
            {loading ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">Loading bookings...</p>
              </div>
            ) : bookings.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg shadow-sm border">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-10 h-10 text-gray-400" />
                </div>
                <p className="text-gray-500">
                  No bookings assigned to this staff.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {paginatedBookings.map((b) => {
                  const { date, slotTime } = getFormattedBookingDateTime({
                    slotAvailabilityId: b?.slotAvailabilityId,
                    slotSnapshot: b?.slotSnapshot,
                  });
                  return (
                    <div
                      key={b._id}
                      className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col gap-3 hover:shadow-lg transition-all shadow-sm"
                    >
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                        <div className="flex items-center gap-2 text-sm font-medium">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <User size={16} className="text-blue-600" />
                          </div>
                          <span className="text-gray-800">
                            {b.userId?.name}
                          </span>
                        </div>
                        <StatusBadge status={b.status} />
                      </div>

                      <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                        <span className="font-medium text-gray-700">
                          Services:{" "}
                        </span>
                        {b.services.map((s) => s.serviceId?.name).join(", ")}
                      </div>

                      <div className="flex flex-wrap gap-4 text-sm">
                        <div className="flex items-center gap-1.5 text-gray-600">
                          <Calendar size={14} />
                          <span>{date}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-gray-600">
                          <Clock size={14} />
                          <span>{slotTime}</span>
                        </div>
                        <div className="flex items-center gap-1 font-semibold text-green-600 ml-auto">
                          <IndianRupee size={14} />
                          <span>{b.totalAmount}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {totalBookingPages > 1 && (
              <div className="flex items-center justify-between mt-6 px-2">
                <p className="text-xs text-gray-600">
                  Page {bookingPage} of {totalBookingPages}
                </p>

                <div className="flex gap-2">
                  <button
                    disabled={bookingPage === 1}
                    onClick={() => setBookingPage((p) => p - 1)}
                    className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition"
                  >
                    Prev
                  </button>

                  <button
                    disabled={bookingPage === totalBookingPages}
                    onClick={() => setBookingPage((p) => p + 1)}
                    className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

          </>
        )}
      </div>
    </div>
  );
};

const InfoCard = ({ label, value, icon: Icon, capitalize }) => (
  <div className="border rounded-xl p-4 bg-white shadow-sm flex items-start gap-3">
    <Icon size={18} className="text-primaryBtnColor mt-1" />
    <div>
      <div className="text-xs text-gray-500 font-medium">{label}</div>
      <div className="text-sm text-gray-900">
        {capitalize && typeof value === "string"
          ? value.charAt(0).toUpperCase() + value.slice(1)
          : value}
      </div>
    </div>
  </div>
);

const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="rounded-xl p-4 bg-white border flex items-center gap-3 shadow-sm">
    <div
      className={`${color} w-10 h-10 rounded-lg flex items-center justify-center text-white`}
    >
      <Icon size={18} />
    </div>

    <div>
      <div className="text-xs text-gray-500 font-medium">{label}</div>
      <div className="text-sm font-semibold text-gray-900">{value}</div>
    </div>
  </div>
);

export default StaffDetails;